import config from "../Config/config";

export function toSentenceCaseWithExceptions(text) {
   // List of words to keep in their original case
   const exceptions = config.exception.wordsInSentence;

   if (typeof text !== 'string') {
      return '';
   }

   // Use a regular expression to split the text into sentences
   const sentences = text.split(/(\.\s|\?\s|\!\s)/);

   // Function to capitalize the first letter of each sentence
   function capitalizeFirstLetter(sentence) {
      return sentence.charAt(0).toUpperCase() + sentence.slice(1);
   }

   // Iterate through the sentences and apply transformations
   for (let i = 0; i < sentences.length; i++) {
      // Trim any leading or trailing white space
      sentences[i] = sentences[i].trim();

      // Check if the sentence is not an exception
      if (!exceptions.includes(sentences[i])) {
         // Convert the sentence to lowercase
         sentences[i] = sentences[i].toLowerCase();
      }

      // Capitalize the first letter of the sentence
      sentences[i] = capitalizeFirstLetter(sentences[i]);
   }

   // Join the sentences back together
   const result = sentences.join('');

   return result;
}

export function toSentenceCase(inputString) {
   // Check if the input is undefined
   if (typeof inputString !== "string") {
      return "";
   }
   // Split the string into parts separated by spaces
   const parts = inputString.split(" ");

   // Capitalize the first letter of each part
   const sentenceCaseParts = parts.map(part => {
      if (part.includes(".")) {
         // Preserve uppercase letters after periods
         return part.replace(/(?:\.|^)\s*(.)/g, (_, char) => char.toUpperCase());
      } else {
         return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
   });

   // Join the parts back together to form the sentence case string
   const sentenceCaseString = sentenceCaseParts.join(" ");

   return sentenceCaseString;
}

/**
 * Formats a number or string based on specific rules:
 * 1. If the input is a number, it is formatted with commas and rounded to 2 decimal places.
 * 2. If the input is a string representing a number, it is converted to a number and then formatted.
 * 3. If the input is not a valid number, it is returned as is.
 *
 * @param {number|string} input - The number or string to be formatted.
 * @returns {string} The formatted string based on the specified rules.
 *
 * @example
 * // Examples:
 * formatNumberOrString(1000);          // Returns "1,000.00"
 * formatNumberOrString("1000");        // Returns "1,000.00"
 * formatNumberOrString(1000.113);      // Returns "1,000.11"
 * formatNumberOrString("1000.113");    // Returns "1,000.11"
 * formatNumberOrString("abc");         // Returns "abc"
 */
export function formatNumberOrStringAfterTwoDecimals(input) {
   if (!input) return 0.00;
   // Convert input to a number if it's a string
   const numValue = typeof input === 'string' ? parseFloat(input) : input;

   // Check if the input is a valid number
   if (!isNaN(numValue)) {
      // Format the number with commas and rounded to 2 decimal places
      const formattedNumber = numValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
      return formattedNumber;
   } else {
      // If not a valid number, return the input as is
      return input.toString();
   }
}

/**
 * Formats a number or string based on specific rules:
 * 1. If the input is a number, it is formatted with commas.
 * 2. If the input is a string representing a number, it is converted to a number and then formatted.
 * 3. If the input is not a valid number, it is returned as is.
 *
 * @param {number|string} input - The number or string to be formatted.
 * @returns {string} The formatted string based on the specified rules.
 *
 * @example
 * // Examples:
 * formatNumberOrString(1000);          // Returns "1,000"
 * formatNumberOrString("1000");        // Returns "1,000"
 * formatNumberOrString(1000.113);      // Returns "1,000.11"
 * formatNumberOrString("1000.113");    // Returns "1,000.11"
 * formatNumberOrString("abc");         // Returns "abc"
 */
export function formatNumberOrString(input) {
   const numValue = typeof input === 'string' ? parseFloat(input) : input;

   if (!isNaN(numValue)) {
      // If the number is a whole number, format without decimal places
      const formattedNumber = numValue % 1 === 0 ? numValue.toLocaleString() : numValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
      return formattedNumber;
   } else {
      return input.toString();
   }
}

export function formatPhoneNumber(digits) {
   // Remove any non-numeric characters
   if (!digits) return "";

   const numericOnly = digits.toString().replace(/\D/g, '');

   // Check if the input has 10 digits
   if (numericOnly.length !== 10) {
      return 'Invalid phone number';
   }

   // Format the phone number with hyphens
   const formattedNumber = `${numericOnly.slice(0, 3)}-${numericOnly.slice(3, 6)}-${numericOnly.slice(6)}`;

   return formattedNumber;
}

export const getOriginalPhNumFromFormattedPhNum = (formattedPhoneNumber) => {
   return formattedPhoneNumber.replace(/\D/g, ''); // Remove all non-digit characters
};


export function textFieldInputHandler(e, formik, propertyName, maxValue) {
   const val = e.target.value;
   // Remove commas from the input
   const cleanedValue = val.replace(/,/g, '');

   // Check if the cleaned value contains a decimal point
   const hasDecimal = cleanedValue.includes('.');

   // Check if val is a number
   let isNumber;
   let formattedNumber;

   if (hasDecimal) {
      // Handle the first decimal separately
      const [integerPart, decimalPart] = cleanedValue.split('.');
      isNumber = !isNaN(parseFloat(integerPart)) && isFinite(integerPart);
      formattedNumber = `${isNumber ? parseFloat(integerPart).toLocaleString('en-US', {
         minimumFractionDigits: 0,
         maximumFractionDigits: 0
      }) : '0'}.${decimalPart.slice(0, 2)}`;
   } else {
      // Use BigInt for large integer values
      isNumber = /^-?\d+$/.test(cleanedValue);
      formattedNumber = isNumber ? BigInt(cleanedValue).toLocaleString('en-US') : '0';
   }

   let resultValue = formattedNumber;

   if (maxValue !== undefined) {
      // If maxValue is provided, limit the result value to it or Infinity (whichever is lower)
      const maxValueLimit = Math.min(maxValue, Infinity);
      if (parseFloat(formattedNumber.replace(/,/g, '')) > maxValueLimit) {
         formik.setFieldError(propertyName, `${e.target.title || "Input"} must be less than or equal to ${formatNumberOrStringAfterTwoDecimals(maxValue)}`);
         return;
      }
   }

   // Update Formik state with the result value
   formik.setFieldValue(propertyName, resultValue);

   // Check if the number exceeds the specified maximum value
   if (isNumber && maxValue && parseFloat(formattedNumber) > maxValue) {
      // Set an error in Formik if the value exceeds the maximum
      formik.setFieldError(propertyName, `${e.target.title || "Input"} must be less than or equal to ${maxValue}`);
   } else if (isNumber) {
      // Reset errors
      formik.setFieldError(propertyName, undefined);
      return resultValue;
   } else {
      // Set an error in Formik
      formik.setFieldError(propertyName, `${e.target.title || "Input"} must be a number`);
   }
}

export function formattedStringToNumber(formattedString) {
   const number = parseFloat(formattedString.replace(/,/g, ''));
   return number;
}

export function profileActiveStatus(profileData) {
   if (!profileData || profileData.isActive === undefined) {
      return "loading";
   }
   else if (profileData.isActive) {
      return "active";
   } else {
      return "inactive";
   }
}


export function phoneNumberTestFunction(value) {
   if (!value) return false;
   const formattedValue = value.replace(/[ -]/g, '');
   const isValidUSNumber = /^\+?1?[2-9]\d{2}[2-9](?!11)\d{6}$/;
   return isValidUSNumber.test(formattedValue);
}


export const PHONE_NUM_REGEX = /^\d{3}-\d{3}-\d{4}$/;


export default {
   toSentenceCaseWithExceptions,
   toSentenceCase,
   formatNumberOrStringAfterTwoDecimals,
   formatNumberOrString,
   formatPhoneNumber,
   textFieldInputHandler,
   profileActiveStatus,
   phoneNumberTestFunction,
   PHONE_NUM_REGEX,
   getOriginalPhNumFromFormattedPhNum,
   formattedStringToNumber
}
